// @ts-nocheck
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import CommentList from 'js/components/CommentList'
import { CoverImage } from 'js/components/CoverImage'
import Document from 'js/components/Document'
import { Col, Container, Row } from 'js/components/Grid'
import { H4 } from 'js/components/Heading'
import ItemHeader from 'js/components/Item/ItemHeader/ItemHeader'
import ItemSuggestedItems from 'js/components/Item/ItemSuggestedItems'
import ItemTags from 'js/components/ItemTags'
import LikeAndBookmark from 'js/components/LikeAndBookmark'
import Section from 'js/components/Section/Section'
import SocialShare from 'js/components/SocialShare/SocialShare'
import TiptapView from 'js/components/Tiptap/TiptapView'
import { useSiteStore } from 'js/lib/stores/SiteStore'
import ThemeProvider from 'js/theme/ThemeProvider'

import Attachments from './components/Attachments'
import ToggleTranslation from './components/ToggleTranslation'
import styles from './ItemLayout.module.css'

const ItemLayout = ({
    viewer,
    entity,
    refetchQueries,
    canUpvote,
    onEdit,
    onAfterDelete,
    options = {
        author: true,
        publishDate: true,
        views: true,
        save: true,
        likes: true,
        follow: true,
        share: true,
        comments: true,
    },
    children,
}) => {
    const { t } = useTranslation()
    const { site } = useSiteStore()

    const {
        localTitle,
        title,
        group,
        featured,
        localRichDescription,
        richDescription,
        tags,
        tagCategories,
        statusPublished,
        commentCount,
        suggestedItems,
        isTranslationEnabled,
        backgroundColor,
        flowAttachments,
    } = entity

    const hasTranslations = !!localTitle && isTranslationEnabled
    const [isTranslated, setIsTranslated] = useState(hasTranslations)

    const translatedTitle = isTranslated ? localTitle : title
    const translatedRichDescription = isTranslated
        ? localRichDescription
        : richDescription

    const isPublished = statusPublished === 'published'
    const hasSuggestedItems = suggestedItems?.length > 0

    // White background won't activate custom theme
    const hasCustomTheme =
        backgroundColor && !['#ffffff', 'white'].includes(backgroundColor)

    const showComments = options.comments
    const showShare = options.share && site.enableSharing && isPublished

    return (
        <ThemeProvider
            theme={hasCustomTheme && backgroundColor}
            bodyColor={backgroundColor || '#FFFFFF'}
        >
            <Document title={translatedTitle} containerTitle={group?.name} />

            <Section grow className={styles.wrapper}>
                <Container size="large" className="ItemCoverContainer">
                    <CoverImage featured={featured} />
                </Container>
                <Container size="normal">
                    <article
                        style={{
                            position: 'relative',
                            marginTop: '32px',
                        }}
                    >
                        <ItemHeader
                            site={site}
                            viewer={viewer}
                            title={translatedTitle}
                            entity={entity}
                            onEdit={onEdit}
                            onAfterDelete={onAfterDelete}
                            refetchQueries={refetchQueries}
                            options={options}
                            hasCustomTheme={hasCustomTheme}
                            children={children}
                        />

                        <TiptapView
                            content={translatedRichDescription}
                            style={{ marginTop: '24px' }}
                        />

                        {hasTranslations && (
                            <ToggleTranslation
                                isTranslated={isTranslated}
                                setIsTranslated={setIsTranslated}
                                style={{ marginTop: '12px' }}
                            />
                        )}

                        <ItemTags
                            showCustomTags={site.showCustomTagsInDetail}
                            showTags={site.showTagsInDetail}
                            customTags={tags}
                            tagCategories={tagCategories}
                            style={{ marginTop: '24px' }}
                        />

                        {isPublished && viewer.loggedIn && (
                            <LikeAndBookmark
                                entity={entity}
                                options={options}
                            />
                        )}

                        <Attachments attachments={flowAttachments} />

                        {showShare && <SocialShare />}
                    </article>
                </Container>

                <Container size={hasSuggestedItems ? 'large' : 'normal'}>
                    <Row style={{ marginTop: '32px' }}>
                        <Col tabletUp={hasSuggestedItems ? 2 / 3 : 1}>
                            {showComments && (
                                <>
                                    <H4 as="h2">
                                        {t('comments.count-comments', {
                                            count: commentCount,
                                        })}
                                    </H4>
                                    <CommentList
                                        viewer={viewer}
                                        entity={entity}
                                        refetchQueries={refetchQueries}
                                        canUpvote={canUpvote}
                                    />
                                </>
                            )}
                        </Col>
                        {hasSuggestedItems && (
                            <Col tabletUp={1 / 3}>
                                <ItemSuggestedItems entity={entity} />
                            </Col>
                        )}
                    </Row>
                </Container>
            </Section>
        </ThemeProvider>
    )
}

export default ItemLayout
